import React, { useMemo } from 'react';
import ReactEcharts from 'echarts-for-react';
import { useHistory, useIntl } from 'umi';
import { formatter } from '@/pages/opinion/components/ChartCommon/formatter';
import {
  contrastString,
  initialUpperCase,
  mathFormat,
  sentimentColorList,
  sentimentType,
} from '@op/utils';
import { getFeedsKeywordText } from '@op/utils/languageValue';
import useKeywordWordCloud from '@op/hooks/useKeywordWordCloud';
import CardCommon from '@op/components/CardCommon';
import SvgIcon from '@home/components/SvgIcon';
import { useToOpinionPage } from '../../utils';
import { goStatsDetailAction } from '@op/pages/stats/Detail';
import { useGetSingePageSourceRule } from '@op/hooks/useGetPageSourceRule';
import { brandHealthFormat, sentimentFormat } from '@op/utils/value';
import { objToUrl } from '@op/utils/url';
import { JumpType } from '@home/utils/utils';

export default function Keyword({
  loading,
  feedsKeyword,
  language = 'origin',
  filter,
  needBrandHealth=false,
  value,
}: any) {
  const { formatMessage } = useIntl();

  const history = useHistory();
  const height = 165;

  useKeywordWordCloud({
    data: feedsKeyword,
    parentClassName: '.word-cloud-wrapper',
  });

  // translateKeywordMap
  const [wordData, empty] = useMemo(() => {
    function getSentimentColorIndex(sentiment_rating: number) {
      return (sentimentType(sentiment_rating, true) as number) - 1;
    }

    const translateKeywordMap = { origin: {}, en: {}, zh: {} };
    const feedsWordData = (feedsKeyword || []).map?.((v: any) => {
      const keywordKey = v.keyword.toLocaleLowerCase();
      translateKeywordMap.origin = {
        ...translateKeywordMap.origin,
        [keywordKey]: v.keyword,
      };
      translateKeywordMap.en = {
        ...translateKeywordMap.en,
        [keywordKey]: v.keyword_en,
      };
      translateKeywordMap.zh = {
        ...translateKeywordMap.zh,
        [keywordKey]: v.keyword_cn,
      };

      const item: any = {
        name: getFeedsKeywordText(v, language),
        value: v.count,
        mentions: v.count,
        sentiment: v.sentiment_rating,
        colorIndex: getSentimentColorIndex(v.sentiment_rating),
      };

      if (language === 'origin' && contrastString(v.keyword, v.keyword_en)) {
        v.child?.sort(
          (a: { cnt: any; }, b: { cnt: any; }) => Number(b.cnt) - Number(a.cnt),
        );
        const origin = v.child?.[0] || v;
        item.originName = initialUpperCase(origin.keyword);
        item.enName = v.keyword_en;
        item.showMore = !contrastString(item.originName, item.enName);
      }

      return item;
    });

    return [feedsWordData, feedsWordData.length === 0];
  }, [feedsKeyword, language]);

  const { Keyword_Color } = useGetSingePageSourceRule({
    keys: 'Keyword_Color',
  });

  const wordOption = () => {
    const option = {
      // tooltip: {
      //   show: false,
      // },
      tooltip: {
        formatter: (params) => {
          const { name, originName, enName, mentions, sentiment, showMore } =
            params.data || {};

          const title = showMore ? '' : name;
          const addMore = showMore
            ? [
              {
                type: formatMessage({ id: 'English' }),
                score: enName,
              },
              {
                type: formatMessage({ id: 'Original Language' }),
                score: originName,
              },
            ]
            : [];

          const table = {
            records: [
              ...addMore,
              {
                type: formatMessage({ id: 'Mentions' }),
                score: mathFormat(mentions),
              },
              ...(Keyword_Color
                ? []
                : [
                  {
                    type: formatMessage({
                      id: needBrandHealth ? 'Brand Health' : 'Sentiment',
                    }),
                    score: needBrandHealth
                      ? brandHealthFormat(sentiment)
                      : sentimentFormat(sentiment),
                  },
                ]),
            ],
            showHeader: false,
            columns: [
              {
                key: 'type',
              },
              {
                key: 'score',
                weight: 'bold',
                align: 'right',
              },
            ],
          };

          return formatter({
            title,
            tables: [table],
          });
        },
        borderWidth: 0,
        confine: true,
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        padding: 0,
        textStyle: {
          fontSize: 12,
        },
      },
      grid: {
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
      },
      series: [
        {
          type: 'wordCloud',
          gridSize: 20,
          sizeRange: [12, 36],
          rotationRange: [0, 0, 0, 0],
          layoutAnimation: false,
          textStyle: {
            color(param: any) {
              const { colorIndex } = param.data;
              return sentimentColorList[colorIndex];
            },
          },
          left: 'center',
          top: 'center',
          width: '70%',
          height: '80%',
          right: null,
          bottom: null,
          drawOutOfBound: false,
          shrinkToFit: true,
          data: wordData || [],
        },
      ],
    };
    return option;
  };

  const { goToOtherPage } = useToOpinionPage({
    filter,
    key: 'Keyword',
    subKey: value,
  });

  const onChartClick = (params: any) => {
    const keywordItem = feedsKeyword?.find(
      (v: any) => v.keyword == params?.name,
    );

    const keywordChild = keywordItem?.child;

    let keyword = [];

    if (Array.isArray(keywordChild) && keywordChild?.length > 0) {
      keyword = keywordChild.map((item) => item.keyword);
    }
    goStatsDetailAction({
      params: {
        filter: {
          ...filter,
          keyword,
          // 以下为舆情固定参数
          region: [],
          region_type: 'region',
          logic: 'and',
          language: [],
          channel_comments: [],
          channel_news: [],
          channel_social: [],
          channel_securitys: [],
          sentiment: [],
          reviews_rating: [],
          social_media_by_account: undefined,
          social_media_by_keyword: undefined,
          topic: [],
          steam_ids: [],
          starred_tag: 0,
          refunded: 0,
          free_played: 0,
          early_access: 0,
          funnys: [],
          helpfuls: [],
          record_times: [],
          review_times: [],
          is_valid: [],
          review_types: [],
          engagements: [],
          comments: [],
          likes: [],
          shares: [],
          monitor_scource: [],
          exclude_hashtag: true,
          need_brand_health: false,
        },
        statsDetailType: 'performance-keyword',
        needBrandHealth: false,
      },
      path: '/opinion/Stats/Detail',
      extra: '&' + objToUrl({ gameid: filter.edition_unified_id }),
      history,
      jumpType: JumpType.Window,
    });
  };

  const canJump = !(loading || empty);

  return (
    <div
      className="dbh-studio-keyword"
      onClick={() => canJump && goToOtherPage()}
    >
      <div className="dbh-studio-keyword-header">
        <div className="dbh-studio-keyword-title">
          {formatMessage({ id: 'Keyword' })}
        </div>
        {canJump && (
          <SvgIcon type="studio-arrow" className="dbh-trend-card-title-icon" />
        )}
      </div>
      <CardCommon
        contentCard
        loading={loading}
        loadingHeight={height}
        empty={empty}
        hasEmptyDesc={false}
        emptyHeight={height}
        className="dbh-studio-keyword-content word-cloud-wrapper"
      >
        <div onClick={(e)=> e?.stopPropagation()}>
          <ReactEcharts
            style={{ height }}
            option={wordOption()}
            lazyUpdate={true}
            opts={{ renderer: 'svg' }}
            notMerge={true}
            onEvents={{ click: onChartClick }}
          />
        </div>
      </CardCommon>
    </div>
  );
}
